/*
 * ─── เทมเพลตสำเร็จรูป (เทมเพลต → แท็บ "สำเร็จรูป", choices 26 ต.ค. 2569: ค ชุดสำเร็จ + ของกลุ่ม (+ รูป AI ใน PR ถัดไป) ·
 * หมวด ร้านอาหาร/กาแฟ/เมนู · โปรโมชั่น · เกษตร (โพสต์ ไม่มีฉลาก/กระสอบ) · โซเชียล/เทศกาลไทย · ถามทุกครั้งเมื่อกด ·
 * ขนาดหน้าตามเทมเพลต).
 * Every template is drawn here by code (none is copied from another app). No DOM, no Fabric — the tests load this;
 * src/templates/build.ts turns one into page objects.
 *
 * A template is laid out on a frame 1080 units wide (its height: 1080 × h ÷ w); build.ts scales it to w × h px.
 * Pictures are empty "ช่องใส่รูป" (`photo`): the member puts their own picture in (or, later, one made by AI from `hint`).
 */
import type { TextFx } from '../editor/textFx';
import type { DocField, FieldType } from '../docs/docFields';
import type { ProfileKey } from '../docs/docProfile';

type Fx = Omit<TextFx, 'ink'>;

/**
 * เอกสาร (D3, 28 ต.ค. 2569): a ช่องข้อมูล of a document template (src/docs/docFields.ts) — its key, its label in
 * both languages, its type; `src` = filled from ข้อมูลบริษัท/แบรนด์. Built empty (FIELD_EMPTY), in Thai.
 */
export interface TplField { k: string; th: string; en: string; type: FieldType; req?: true; src?: ProfileKey; opts?: { th: string[]; en: string[] } }
/** เอกสาร: a template's field as stored on the page (Thai first, empty) — checked by tests/doc.test.ts with checkField */
export const tplDocField = (f: TplField): DocField => ({
  k: f.k, label: { th: f.th, en: f.en }, type: f.type, l: 'th',
  ...(f.req ? { req: true as const } : {}), ...(f.src ? { src: f.src } : {}), ...(f.opts ? { opts: f.opts } : {})
});
/** a table cell of a template: its words (in both languages when it is a heading/item), its look */
export type TplCell = string | { th: string; en: string; b?: true; al?: 'l' | 'c' | 'r' };
export type TplEl =
  /** a box; `lock`: the page's background (at the bottom, locked) */
  | { k: 'rect'; x: number; y: number; w: number; h: number; fill: string; r?: number; stroke?: string; sw?: number; op?: number; angle?: number; lock?: boolean }
  | { k: 'ellipse'; x: number; y: number; w: number; h: number; fill: string; op?: number }
  /** a shape of องค์ประกอบ → รูปทรง (src/editor/shapeLib.ts) */
  | { k: 'shape'; id: string; x: number; y: number; w: number; h: number; fill: string; angle?: number; op?: number }
  | { k: 'line'; x1: number; y1: number; x2: number; y2: number; ink: string; sw: number; dash?: boolean }
  /** a text box: top-left at x, y, `w` wide; size in units */
  | { k: 'text'; x: number; y: number; w: number; text: string; font: string; weight: number; size: number; ink: string;
      align?: 'left' | 'center' | 'right'; lh?: number; sp?: number; fx?: Fx;
      /** AI สร้างเทมเพลต only: the box's height — words that need more are made smaller to fit (build.ts) */
      fitH?: number;
      /** เอกสาร: the same words in English (the document's language can be switched) */
      en?: string;
      /** เอกสาร: this text is a ช่องข้อมูล (its words are the value) */
      f?: TplField }
  /** ช่องใส่รูป: `hint` says what picture goes there (shown on it, and the AI's starting words later) */
  | { k: 'photo'; x: number; y: number; w: number; h: number; hint: string; shape?: 'rect' | 'round' | 'circle';
      /** เอกสาร: the company's logo goes here (a field of type 'logo') */
      f?: TplField;
      /** เอกสาร: no AI picture here (a logo, a signature: the member's own only) */
      noAi?: true }
  /** ตาราง (src/table/tableModel.ts): `cols` are shares of `w`; the first `head` rows are headings (bold, grey) */
  | { k: 'table'; x: number; y: number; w: number; cols: number[]; rows: TplCell[][]; head: number; size: number };

export interface BuiltinTemplate {
  id: string; th: string; cat: TplCatId;
  /** more words it is found by (its name and category are searched too) */
  kw: string;
  w: number; h: number;
  /** the page's colour (a locked box at the bottom) */
  bg: string;
  els: TplEl[];
}
export type TplCatId = 'food' | 'promo' | 'agri' | 'social' | 'doc';
export const TPL_CATS: Array<{ id: TplCatId; th: string; kw: string }> = [
  { id: 'food', th: 'ร้านอาหาร · กาแฟ · เมนู', kw: 'ร้านอาหาร ร้านกาแฟ คาเฟ่ กาแฟ เมนู อาหาร เครื่องดื่ม ชานม ขนม เบเกอรี่ food cafe coffee menu' },
  { id: 'promo', th: 'โปรโมชั่น · ลดราคา', kw: 'โปรโมชั่น ลดราคา เซลล์ ส่วนลด คูปอง แจกฟรี ของแถม sale promotion discount coupon' },
  { id: 'agri', th: 'เกษตร · สินค้าเกษตร', kw: 'เกษตร ปุ๋ย สวน ทุเรียน ผลไม้ ผัก ไร่ นา ต้นไม้ ฟาร์ม ออร์แกนิก farm agri fertilizer' },
  { id: 'social', th: 'โซเชียล · เทศกาลไทย', kw: 'โซเชียล โพสต์ เทศกาล สงกรานต์ ลอยกระทง ปีใหม่ ตรุษจีน วันแม่ วันพ่อ ขอบคุณ ประกาศ social festival' },
  // เอกสาร (D3, 28 ต.ค. 2569): A4 with ช่องข้อมูล, ไทย/English, ข้อมูลบริษัท (กรอกที่ เพิ่มเติม → กรอกข้อมูลเอกสาร)
  { id: 'doc', th: 'เอกสาร · ใบรับรอง · ใบเสนอราคา', kw: 'เอกสาร ใบรับรอง COA สเปค ใบเสนอราคา ใบส่งสินค้า ผลวิเคราะห์ ดิน ปุ๋ย ฟอร์ม document certificate quotation delivery' }
];

/* ---------- sizes ---------- */
const POST = { w: 1080, h: 1080 }, TALL = { w: 1080, h: 1350 }, STORY = { w: 1080, h: 1920 }, WIDE = { w: 1920, h: 1080 };
/** A4 at 300 DPI (as ขนาดหน้า → A4) */
const A4 = { w: 2480, h: 3508 };

/* ---------- helpers ---------- */
const fx = (kind: Fx['kind'], extra: Partial<Fx> = {}): Fx => ({ kind, ...extra });
const T = (x: number, y: number, w: number, text: string, font: string, weight: number, size: number, ink: string, more: Partial<Extract<TplEl, { k: 'text' }>> = {}): TplEl =>
  ({ k: 'text', x, y, w, text, font, weight, size, ink, ...more });
const R = (x: number, y: number, w: number, h: number, fill: string, more: Partial<Extract<TplEl, { k: 'rect' }>> = {}): TplEl =>
  ({ k: 'rect', x, y, w, h, fill, ...more });
const P = (x: number, y: number, w: number, h: number, hint: string, shape: 'rect' | 'round' | 'circle' = 'rect'): TplEl =>
  ({ k: 'photo', x, y, w, h, hint, shape });
const S = (id: string, x: number, y: number, w: number, h: number, fill: string, more: Partial<Extract<TplEl, { k: 'shape' }>> = {}): TplEl =>
  ({ k: 'shape', id, x, y, w, h, fill, ...more });
const O = (x: number, y: number, w: number, h: number, fill: string, op?: number): TplEl => ({ k: 'ellipse', x, y, w, h, fill, op });
const L = (x1: number, y1: number, x2: number, y2: number, ink: string, sw: number, dash = false): TplEl => ({ k: 'line', x1, y1, x2, y2, ink, sw, dash });

/** the frame's height in units (1080 wide) */
export const frameH = (t: { w: number; h: number }) => 1080 * t.h / t.w;

/* ---------- layouts (each used by a few templates, with its own words and colours) ---------- */
interface Pal { bg: string; ink: string; accent: string; soft: string; deep: string }

/** a picture on top, a title and a line under it, a pill at the bottom (1080×1350) */
function photoTop(p: Pal, title: string, sub: string, pill: string, hint: string, font = 'Kanit'): TplEl[] {
  return [
    P(60, 60, 960, 760, hint, 'round'),
    T(60, 860, 960, title, font, 700, 104, p.ink, { lh: 1.05 }),
    T(60, 1010, 960, sub, 'Sarabun', 400, 44, p.deep),
    T(290, 1150, 500, pill, 'Prompt', 600, 46, '#ffffff', { fx: fx('bg', { color: p.accent, round: 100, offset: 45, alpha: 100 }) })
  ];
}

/** a big title on the left, a round picture on the right, a price badge (1080×1080) */
function splitRound(p: Pal, kicker: string, title: string, price: string, hint: string): TplEl[] {
  return [
    O(540, 150, 600, 600, p.soft),
    P(560, 190, 480, 480, hint, 'circle'),
    T(60, 170, 460, kicker, 'Prompt', 600, 44, p.accent, { align: 'left', sp: 100 }),
    T(60, 240, 470, title, 'Kanit', 700, 108, p.ink, { align: 'left', lh: 1.0 }),
    S('burst12', 60, 640, 300, 300, p.accent),
    T(60, 735, 300, price, 'Kanit', 700, 76, '#ffffff'),
    T(60, 960, 960, 'สั่งได้ที่หน้าร้าน · เดลิเวอรี่', 'Sarabun', 400, 38, p.deep, { align: 'left' })
  ];
}

/** a menu: title, items with prices and dotted lines (A4 or 1080×1350) */
function menuList(p: Pal, title: string, items: Array<[string, string]>, foot: string, hint: string, H: number): TplEl[] {
  const top = 470, row = Math.min(110, (H - top - 260) / items.length);
  const els: TplEl[] = [
    R(0, 0, 1080, 360, p.deep),
    T(60, 90, 960, title, 'Kanit', 700, 120, '#ffffff'),
    T(60, 250, 960, 'MENU', 'Chakra Petch', 700, 46, p.accent, { sp: 600 }),
    P(820, 300, 200, 200, hint, 'circle')
  ];
  items.forEach(([name, price], i) => {
    const y = top + i * row;
    els.push(T(60, y, 640, name, 'Prompt', 600, 48, p.ink, { align: 'left' }));
    els.push(L(60, y + 70, 1020, y + 70, p.soft, 3, true));
    els.push(T(760, y, 260, price, 'Kanit', 700, 48, p.accent, { align: 'right' }));
  });
  els.push(T(60, H - 160, 960, foot, 'Sarabun', 400, 40, p.deep));
  return els;
}

/** a sale: a burst with the percent, a headline, a picture strip (1080×1080) */
function saleBurst(p: Pal, head: string, pct: string, until: string, hint: string): TplEl[] {
  return [
    S('burst24', 300, 70, 480, 480, p.accent, { angle: 8 }),
    T(300, 210, 480, pct, 'Kanit', 700, 150, '#ffffff', { fx: fx('shadow', { color: '#000000', offset: 20, blur: 10, alpha: 35 }) }),
    T(60, 570, 960, head, 'Kanit', 700, 110, p.ink, { fx: fx('echo', { color: p.accent, offset: 35 }) }),
    P(60, 740, 960, 220, hint, 'round'),
    T(60, 985, 960, until, 'Sarabun', 700, 40, p.deep)
  ];
}

/** a story: full picture, a band with the title, a call to action (1080×1920) */
function storyBand(p: Pal, title: string, sub: string, cta: string, hint: string): TplEl[] {
  return [
    P(0, 0, 1080, 1300, hint),
    R(0, 1240, 1080, 680, p.bg),
    S('wave', 0, 1180, 1080, 140, p.bg),
    T(70, 1330, 940, title, 'Kanit', 700, 120, p.ink, { lh: 1.05 }),
    T(70, 1600, 940, sub, 'Sarabun', 400, 46, p.deep),
    T(270, 1730, 540, cta, 'Prompt', 600, 52, '#ffffff', { fx: fx('bg', { color: p.accent, round: 100, offset: 50, alpha: 100 }) })
  ];
}

/** a greeting: words in the middle, shapes around (1080×1080) */
function greeting(p: Pal, big: string, small: string, shape: string, font = 'Charm'): TplEl[] {
  return [
    S(shape, -80, -80, 380, 380, p.accent, { op: 0.9 }),
    S(shape, 800, 780, 380, 380, p.soft, { op: 0.9 }),
    S(shape, 860, 60, 160, 160, p.accent, { op: 0.6 }),
    S(shape, 70, 860, 140, 140, p.soft, { op: 0.6 }),
    T(90, 330, 900, big, font, 700, 150, p.ink, { lh: 1.1, fx: fx('shadow', { color: p.deep, offset: 15, blur: 20, alpha: 25 }) }),
    T(140, 700, 800, small, 'Sarabun', 400, 46, p.deep)
  ];
}

/** tips: a title bar and numbered points (1080×1350) */
function tips(p: Pal, title: string, points: string[], foot: string): TplEl[] {
  const els: TplEl[] = [
    R(60, 60, 960, 250, p.accent, { r: 40 }),
    T(100, 110, 880, title, 'Kanit', 700, 76, '#ffffff', { lh: 1.1 })
  ];
  points.forEach((t, i) => {
    const y = 380 + i * 210;
    els.push(O(60, y, 120, 120, p.soft));
    els.push(T(60, y + 22, 120, String(i + 1), 'Kanit', 700, 64, p.deep));
    els.push(T(220, y + 10, 800, t, 'Prompt', 400, 46, p.ink, { align: 'left', lh: 1.25 }));
  });
  els.push(T(60, 1240, 960, foot, 'Sarabun', 700, 36, p.deep));
  return els;
}

/** an announcement: a frame, a headline, details (1080×1080) */
function notice(p: Pal, kicker: string, head: string, body: string, foot: string): TplEl[] {
  return [
    R(50, 50, 980, 980, 'transparent', { stroke: p.accent, sw: 10, r: 30 }),
    S('bubble-round', 340, 110, 400, 200, p.accent),
    T(340, 160, 400, kicker, 'Prompt', 600, 52, '#ffffff'),
    T(110, 380, 860, head, 'Kanit', 700, 110, p.ink, { lh: 1.1 }),
    T(130, 650, 820, body, 'Sarabun', 400, 46, p.deep, { lh: 1.4 }),
    T(110, 900, 860, foot, 'Prompt', 600, 40, p.accent)
  ];
}

/** a voucher (1920×1080) */
function voucher(p: Pal, value: string, what: string, code: string, hint: string): TplEl[] {
  return [
    P(0, 0, 400, 608, hint),
    R(400, 0, 680, 608, p.bg, { lock: false }),
    L(400, 30, 400, 578, p.accent, 6, true),
    T(440, 50, 600, 'บัตรกำนัล', 'Prompt', 600, 40, p.accent, { align: 'left', sp: 100 }),
    T(440, 110, 600, value, 'Kanit', 700, 130, p.ink, { align: 'left' }),
    T(440, 290, 600, what, 'Sarabun', 400, 38, p.deep, { align: 'left', lh: 1.3 }),
    T(440, 470, 380, code, 'Chakra Petch', 700, 40, '#ffffff', { align: 'left', fx: fx('bg', { color: p.accent, round: 30, offset: 40, alpha: 100 }) })
  ];
}

/** a product post: picture in a blob, name, 3 checks (1080×1080) */
function product(p: Pal, name: string, checks: string[], hint: string): TplEl[] {
  const els: TplEl[] = [
    S('blob1', 520, 120, 560, 560, p.soft),
    P(560, 160, 480, 480, hint, 'circle'),
    T(60, 120, 500, name, 'Kanit', 700, 96, p.ink, { align: 'left', lh: 1.05 })
  ];
  checks.forEach((c, i) => {
    const y = 470 + i * 120;
    els.push(S('star5', 60, y + 8, 56, 56, p.accent));
    els.push(T(140, y, 400, c, 'Prompt', 400, 42, p.deep, { align: 'left' }));
  });
  els.push(R(0, 900, 1080, 180, p.deep));
  els.push(T(60, 945, 960, 'สอบถาม/สั่งซื้อ ทักแชทได้เลย', 'Prompt', 600, 50, '#ffffff'));
  return els;
}

/* ---------- palettes ---------- */
const COFFEE: Pal = { bg: '#f6efe6', ink: '#3b2416', accent: '#c0712b', soft: '#e7d3bd', deep: '#6b4a33' };
const MATCHA: Pal = { bg: '#eef5e6', ink: '#1f3b1a', accent: '#5c9b3a', soft: '#cfe3bd', deep: '#3d5c2f' };
const TOMATO: Pal = { bg: '#fff6ef', ink: '#2b1a12', accent: '#e4572e', soft: '#ffd8c2', deep: '#8a3a1c' };
const BERRY: Pal = { bg: '#fff0f5', ink: '#3a0f24', accent: '#d6336c', soft: '#ffc9dc', deep: '#7a1f45' };
const NIGHT: Pal = { bg: '#14121f', ink: '#ffffff', accent: '#ffd600', soft: '#3a3557', deep: '#cfc9ff' };
const SUN: Pal = { bg: '#fff8db', ink: '#1d1a2b', accent: '#e53935', soft: '#ffe082', deep: '#5d4037' };
const SKY: Pal = { bg: '#eaf4ff', ink: '#0d2a4a', accent: '#1e88e5', soft: '#bbdefb', deep: '#1f4e79' };
const PURPLE: Pal = { bg: '#f3eeff', ink: '#21153f', accent: '#6b3fd9', soft: '#d9ccff', deep: '#4a2f99' };
const FARM: Pal = { bg: '#f4f9ee', ink: '#1b3a12', accent: '#2e7d32', soft: '#c8e6c9', deep: '#33691e' };
const SOIL: Pal = { bg: '#f7f1e8', ink: '#2f2416', accent: '#8d6e63', soft: '#e0d2c0', deep: '#5d4037' };
const DURIAN: Pal = { bg: '#fffbe6', ink: '#2d3a0f', accent: '#c9a227', soft: '#f3e5a6', deep: '#5b6b1f' };
const GOLD: Pal = { bg: '#7a0d12', ink: '#ffe08a', accent: '#ffc107', soft: '#b71c1c', deep: '#ffecb3' };
const WATER: Pal = { bg: '#e6f7fb', ink: '#06394a', accent: '#00a6c8', soft: '#b2ebf2', deep: '#00687e' };
const LOTUS: Pal = { bg: '#fdf2f8', ink: '#4a1036', accent: '#e86aa6', soft: '#f8c8de', deep: '#8f2a63' };
const MOM: Pal = { bg: '#eef3ff', ink: '#1a237e', accent: '#5c6bc0', soft: '#c5cae9', deep: '#303f9f' };
const DAD: Pal = { bg: '#fffde7', ink: '#3e2f00', accent: '#f9a825', soft: '#fff176', deep: '#6d5600' };

/* ---------- เอกสาร (A4, ไทย/English, ช่องข้อมูล) ----------
 * Layout on the 1080 frame (A4 = 1080 × 1527.7): head 50–225 (โลโก้ + ข้อมูลบริษัท), title, the document's own fields,
 * a table, notes, signatures. Values a lab or a seller must give (spec numbers, results, prices) are left empty:
 * nothing technical is made up. ภาษา: every fixed word has its English (`en`); the fill panel switches the page.
 */
const DOC_INK = '#1f2a1c', DOC_SOFT = '#5b6472', DOC_LINE = '#9aa0ad', DOC_ACC = '#2e7d32';
const F = (k: string, th: string, en: string, type: TplField['type'] = 'text', more: Partial<TplField> = {}): TplField => ({ k, th, en, type, ...more });
/** a fixed word in both languages */
const W = (x: number, y: number, w: number, th: string, en: string, size = 19, more: Partial<Extract<TplEl, { k: 'text' }>> = {}): TplEl =>
  T(x, y, w, th, 'Sarabun', 400, size, DOC_SOFT, { align: 'left', en, ...more });
/** a ช่องข้อมูล's value box */
const V = (x: number, y: number, w: number, f: TplField, size = 20, more: Partial<Extract<TplEl, { k: 'text' }>> = {}): TplEl =>
  T(x, y, w, f.th, 'Sarabun', 400, size, DOC_INK, { align: 'left', f, ...more });
/** a label and its value on one line (label width `lw`) */
const LV = (x: number, y: number, w: number, lw: number, f: TplField, th = f.th, en = f.en): TplEl[] =>
  [W(x, y, lw, th, en), V(x + lw, y, w - lw, f)];
const C = (th: string, en: string, more: { b?: true; al?: 'l' | 'c' | 'r' } = {}): TplCell => ({ th, en, ...more });

/** the head of every document: logo, company, a line, the title (centre), the number and date (right) */
function docHead(title: [string, string], no: TplField): TplEl[] {
  return [
    { k: 'photo', x: 60, y: 50, w: 200, h: 120, hint: 'โลโก้บริษัท', shape: 'rect', noAi: true, f: F('logo', 'โลโก้', 'Logo', 'logo') },
    V(290, 50, 730, F('coName', 'ชื่อบริษัท/แบรนด์', 'Company name', 'text', { src: 'name', req: true }), 30, { weight: 700 }),
    V(290, 95, 730, F('coAddress', 'ที่อยู่บริษัท', 'Company address', 'multiline', { src: 'address' }), 17, { lh: 1.25 }),
    W(290, 150, 46, 'โทร', 'Tel.', 17), V(336, 150, 250, F('coPhone', 'โทรศัพท์', 'Phone', 'text', { src: 'phone' }), 17),
    W(600, 150, 64, 'อีเมล', 'Email', 17), V(664, 150, 356, F('coEmail', 'อีเมล', 'Email', 'text', { src: 'email' }), 17),
    W(290, 178, 170, 'เลขผู้เสียภาษี', 'Tax ID', 17), V(460, 178, 200, F('coTax', 'เลขประจำตัวผู้เสียภาษี', 'Tax ID', 'text', { src: 'taxId' }), 17),
    V(680, 178, 340, F('coWeb', 'เว็บไซต์', 'Website', 'text', { src: 'website' }), 17, { align: 'right' }),
    L(60, 225, 1020, 225, DOC_ACC, 4),
    T(60, 250, 960, title[0], 'Sarabun', 700, 40, DOC_INK, { en: title[1] }),
    ...LV(640, 320, 380, 130, no),
    ...LV(640, 352, 380, 130, F('date', 'วันที่', 'Date', 'date', { req: true }))
  ];
}
/** two signatures side by side: a picture slot for a signature (or sign by hand on the line), the name, the date */
function docSigns(y: number, a: [string, string], b: [string, string]): TplEl[] {
  const one = (x: number, who: [string, string], key: string): TplEl[] => [
    { k: 'photo', x: x + 70, y, w: 280, h: 90, hint: 'ลายเซ็น (ใส่รูป หรือเซ็นมือบนเส้น)', shape: 'rect', noAi: true },
    L(x + 30, y + 100, x + 390, y + 100, DOC_LINE, 2),
    W(x, y + 112, 420, who[0], who[1], 19, { align: 'center', weight: 700, ink: DOC_INK }),
    T(x, y + 142, 20, '(', 'Sarabun', 400, 19, DOC_SOFT, { align: 'left' }),
    V(x + 20, y + 142, 380, F(`${key}Name`, 'ชื่อผู้ลงนาม', 'Name'), 19, { align: 'center' }),
    T(x + 400, y + 142, 20, ')', 'Sarabun', 400, 19, DOC_SOFT, { align: 'right' }),
    W(x + 40, y + 174, 90, 'วันที่', 'Date'), V(x + 130, y + 174, 250, F(`${key}Date`, 'วันที่ลงนาม', 'Date signed', 'date'), 19)
  ];
  return [...one(60, a, 'sign1'), ...one(600, b, 'sign2')];
}
const blank = (n: number, cols: number): TplCell[][] => Array.from({ length: n }, () => Array.from({ length: cols }, () => ''));

/** ใบรับรองผลวิเคราะห์ (COA) */
function docCoa(): TplEl[] {
  const info: Array<[TplField, number, number]> = [
    [F('product', 'ชื่อสินค้า', 'Product name', 'text', { req: true }), 60, 410],
    [F('grade', 'สูตร/เกรด', 'Formula / Grade'), 560, 410],
    [F('regNo', 'เลขทะเบียน', 'Registration no.'), 60, 445],
    [F('lot', 'Lot / Batch', 'Lot / Batch', 'text', { req: true }), 560, 445],
    [F('mfgDate', 'วันผลิต', 'Manufacturing date', 'date'), 60, 480],
    [F('expDate', 'วันหมดอายุ', 'Expiry date', 'date'), 560, 480],
    [F('pack', 'ขนาดบรรจุ', 'Pack size'), 60, 515],
    [F('customer', 'ลูกค้า', 'Customer'), 560, 515]
  ];
  return [
    ...docHead(['ใบรับรองผลการวิเคราะห์', 'Certificate of Analysis (COA)'], F('no', 'เลขที่ COA', 'COA No.', 'text', { req: true })),
    ...info.flatMap(([f, x, y]) => LV(x, y, 460, 170, f)),
    { k: 'table', x: 60, y: 570, w: 960, cols: [3, 1.3, 2, 1.6, 2, 1.4], head: 1, size: 18, rows: [
      [C('รายการทดสอบ', 'Test item'), C('หน่วย', 'Unit'), C('ค่ามาตรฐาน', 'Specification'), C('ผลวิเคราะห์', 'Result'), C('วิธีทดสอบ', 'Method'), C('ผล', 'Pass/Fail')],
      [C('ไนโตรเจนทั้งหมด (N)', 'Total nitrogen (N)'), '%', '', '', '', ''],
      [C('ฟอสฟอรัส (P₂O₅)', 'Available phosphate (P₂O₅)'), '%', '', '', '', ''],
      [C('โพแทสเซียม (K₂O)', 'Soluble potash (K₂O)'), '%', '', '', '', ''],
      [C('ความชื้น', 'Moisture'), '%', '', '', '', ''],
      [C('ความเป็นกรด-ด่าง (pH)', 'pH'), '-', '', '', '', ''],
      ...blank(3, 6)
    ] },
    ...LV(60, 910, 960, 170, F('result', 'สรุปผล', 'Conclusion', 'choice', { opts: { th: ['ผ่านตามมาตรฐาน', 'ไม่ผ่านตามมาตรฐาน'], en: ['Conforms to specification', 'Does not conform'] } })),
    W(60, 950, 170, 'หมายเหตุ', 'Remarks'), V(230, 950, 790, F('remark', 'หมายเหตุ', 'Remarks', 'multiline'), 19, { lh: 1.3 }),
    ...docSigns(1240, ['ผู้ตรวจสอบ', 'Inspected by'], ['ผู้อนุมัติ', 'Approved by'])
  ];
}

/** สเปคสินค้า */
function docSpec(): TplEl[] {
  return [
    ...docHead(['ข้อมูลจำเพาะสินค้า', 'Product Specification'], F('no', 'เลขที่เอกสาร', 'Document No.')),
    P(60, 410, 300, 300, 'รูปสินค้า (บรรจุภัณฑ์)', 'round'),
    ...LV(390, 410, 630, 170, F('product', 'ชื่อสินค้า', 'Product name', 'text', { req: true })),
    ...LV(390, 445, 630, 170, F('code', 'รหัสสินค้า', 'Product code')),
    ...LV(390, 480, 630, 170, F('regNo', 'เลขทะเบียน', 'Registration no.')),
    ...LV(390, 515, 630, 170, F('pack', 'ขนาดบรรจุ', 'Pack size')),
    ...LV(390, 550, 630, 170, F('shelf', 'อายุการเก็บ', 'Shelf life')),
    W(390, 590, 170, 'รายละเอียด', 'Description'), V(560, 590, 460, F('desc', 'รายละเอียดสินค้า', 'Product description', 'multiline'), 19, { lh: 1.3 }),
    W(60, 740, 470, 'ธาตุอาหาร / ส่วนประกอบ', 'Nutrients / Composition', 21, { weight: 700, ink: DOC_INK }),
    { k: 'table', x: 60, y: 780, w: 470, cols: [2.4, 1, 1.4], head: 1, size: 18, rows: [
      [C('รายการ', 'Item'), C('หน่วย', 'Unit'), C('ค่า', 'Value')],
      [C('ไนโตรเจน (N)', 'Nitrogen (N)'), '%', ''],
      [C('ฟอสฟอรัส (P₂O₅)', 'Phosphate (P₂O₅)'), '%', ''],
      [C('โพแทสเซียม (K₂O)', 'Potash (K₂O)'), '%', ''],
      ...blank(3, 3)
    ] },
    W(550, 740, 470, 'คุณสมบัติทางกายภาพ', 'Physical properties', 21, { weight: 700, ink: DOC_INK }),
    { k: 'table', x: 550, y: 780, w: 470, cols: [2, 2], head: 1, size: 18, rows: [
      [C('คุณสมบัติ', 'Property'), C('ค่า', 'Value')],
      [C('ลักษณะ', 'Appearance'), ''],
      [C('สี', 'Color'), ''],
      [C('ความชื้น (%)', 'Moisture (%)'), ''],
      [C('ความเป็นกรด-ด่าง (pH)', 'pH'), ''],
      ...blank(2, 2)
    ] },
    W(60, 1090, 170, 'การเก็บรักษา', 'Storage'), V(230, 1090, 790, F('storage', 'การเก็บรักษา', 'Storage', 'multiline'), 19, { lh: 1.3 }),
    W(60, 1150, 170, 'วิธีใช้', 'Directions'), V(230, 1150, 790, F('usage', 'วิธีใช้', 'Directions for use', 'multiline'), 19, { lh: 1.3 }),
    ...docSigns(1260, ['ผู้จัดทำ', 'Prepared by'], ['ผู้อนุมัติ', 'Approved by'])
  ];
}

/** the item table of a quotation / delivery note */
const itemRows = (n: number, cols: number): TplCell[][] => Array.from({ length: n }, (_, i) => [String(i + 1), ...Array.from({ length: cols - 1 }, () => '')]);

/** ใบเสนอราคา */
function docQuote(): TplEl[] {
  return [
    ...docHead(['ใบเสนอราคา', 'Quotation'], F('no', 'เลขที่', 'Quotation No.', 'text', { req: true })),
    ...LV(60, 320, 560, 130, F('customer', 'ลูกค้า', 'Customer', 'text', { req: true })),
    W(60, 355, 130, 'ที่อยู่', 'Address'), V(190, 355, 430, F('custAddress', 'ที่อยู่ลูกค้า', 'Customer address', 'multiline'), 19, { lh: 1.25 }),
    ...LV(60, 445, 560, 130, F('contact', 'ผู้ติดต่อ', 'Attention')),
    ...LV(640, 384, 380, 130, F('validUntil', 'ยืนราคาถึง', 'Valid until', 'date')),
    { k: 'table', x: 60, y: 500, w: 960, cols: [0.7, 4.3, 1.1, 1.1, 1.6, 1.8], head: 1, size: 18, rows: [
      [C('ลำดับ', 'No.'), C('รายการ', 'Description'), C('จำนวน', 'Qty'), C('หน่วย', 'Unit'), C('ราคา/หน่วย', 'Unit price'), C('จำนวนเงิน', 'Amount')],
      ...itemRows(10, 6)
    ] },
    ...LV(640, 910, 380, 190, F('subtotal', 'รวมเป็นเงิน', 'Subtotal', 'number'), 'รวมเป็นเงิน', 'Subtotal'),
    ...LV(640, 945, 380, 190, F('vat', 'ภาษีมูลค่าเพิ่ม 7%', 'VAT 7%', 'number')),
    ...LV(640, 980, 380, 190, F('total', 'รวมทั้งสิ้น', 'Grand total', 'number', { req: true })),
    W(60, 910, 170, 'เงื่อนไข', 'Terms'), V(60, 945, 540, F('terms', 'เงื่อนไขการชำระเงิน/ส่งสินค้า', 'Payment / delivery terms', 'multiline'), 19, { lh: 1.3 }),
    ...docSigns(1260, ['ผู้เสนอราคา', 'Quoted by'], ['ผู้อนุมัติสั่งซื้อ', 'Accepted by'])
  ];
}

/** ใบส่งสินค้า */
function docDelivery(): TplEl[] {
  return [
    ...docHead(['ใบส่งสินค้า', 'Delivery Note'], F('no', 'เลขที่', 'Delivery No.', 'text', { req: true })),
    ...LV(60, 320, 560, 130, F('customer', 'ลูกค้า', 'Customer', 'text', { req: true })),
    W(60, 355, 130, 'ส่งที่', 'Ship to'), V(190, 355, 430, F('shipTo', 'ที่อยู่จัดส่ง', 'Delivery address', 'multiline'), 19, { lh: 1.25 }),
    ...LV(60, 445, 560, 130, F('phone', 'โทรศัพท์', 'Phone')),
    ...LV(640, 384, 380, 130, F('poNo', 'เลขที่ PO', 'PO No.')),
    ...LV(640, 416, 380, 130, F('carrier', 'ขนส่งโดย', 'Carrier')),
    { k: 'table', x: 60, y: 500, w: 960, cols: [0.7, 4.6, 1.3, 1.2, 2.6], head: 1, size: 18, rows: [
      [C('ลำดับ', 'No.'), C('รายการ', 'Description'), C('จำนวน', 'Qty'), C('หน่วย', 'Unit'), C('หมายเหตุ', 'Remarks')],
      ...itemRows(12, 5)
    ] },
    W(60, 980, 170, 'หมายเหตุ', 'Remarks'), V(230, 980, 790, F('remark', 'หมายเหตุ', 'Remarks', 'multiline'), 19, { lh: 1.3 }),
    W(60, 1060, 960, 'ได้รับสินค้าตามรายการข้างต้นในสภาพเรียบร้อย', 'Goods received in good order and condition', 19),
    ...docSigns(1260, ['ผู้ส่งสินค้า', 'Delivered by'], ['ผู้รับสินค้า', 'Received by'])
  ];
}

/** ใบรายงานผลวิเคราะห์ดิน / ปุ๋ย */
function docSoil(): TplEl[] {
  return [
    ...docHead(['รายงานผลการวิเคราะห์', 'Analysis Report'], F('no', 'เลขที่รายงาน', 'Report No.', 'text', { req: true })),
    ...LV(60, 410, 460, 170, F('owner', 'ชื่อเกษตรกร/ลูกค้า', 'Farmer / Customer', 'text', { req: true }), 'ผู้ส่งตัวอย่าง', 'Submitted by'),
    ...LV(560, 410, 460, 170, F('sample', 'ชนิดตัวอย่าง', 'Sample type', 'choice', { opts: { th: ['ดิน', 'ปุ๋ย', 'น้ำ'], en: ['Soil', 'Fertilizer', 'Water'] } })),
    ...LV(60, 445, 460, 170, F('place', 'สถานที่เก็บตัวอย่าง', 'Sampling location')),
    ...LV(560, 445, 460, 170, F('sampleDate', 'วันเก็บตัวอย่าง', 'Sampling date', 'date')),
    ...LV(60, 480, 460, 170, F('crop', 'พืชที่ปลูก', 'Crop')),
    ...LV(560, 480, 460, 170, F('sampleId', 'รหัสตัวอย่าง', 'Sample ID')),
    { k: 'table', x: 60, y: 540, w: 960, cols: [3.2, 1.6, 1.6, 2, 2], head: 1, size: 18, rows: [
      [C('รายการวิเคราะห์', 'Parameter'), C('หน่วย', 'Unit'), C('ผลวิเคราะห์', 'Result'), C('ระดับ', 'Level'), C('วิธีวิเคราะห์', 'Method')],
      [C('ความเป็นกรด-ด่าง (pH)', 'pH'), '-', '', '', ''],
      [C('อินทรียวัตถุ (OM)', 'Organic matter (OM)'), '%', '', '', ''],
      [C('ฟอสฟอรัสที่เป็นประโยชน์ (P)', 'Available phosphorus (P)'), 'mg/kg', '', '', ''],
      [C('โพแทสเซียมที่แลกเปลี่ยนได้ (K)', 'Exchangeable potassium (K)'), 'mg/kg', '', '', ''],
      [C('แคลเซียม (Ca)', 'Calcium (Ca)'), 'mg/kg', '', '', ''],
      [C('แมกนีเซียม (Mg)', 'Magnesium (Mg)'), 'mg/kg', '', '', ''],
      [C('การนำไฟฟ้า (EC)', 'Electrical conductivity (EC)'), 'dS/m', '', '', ''],
      ...blank(2, 5)
    ] },
    W(60, 910, 960, 'คำแนะนำ', 'Recommendations', 21, { weight: 700, ink: DOC_INK }),
    V(60, 948, 960, F('advice', 'คำแนะนำการปรับปรุงดิน/การใส่ปุ๋ย', 'Soil / fertilizer recommendations', 'multiline'), 19, { lh: 1.3 }),
    ...docSigns(1260, ['ผู้วิเคราะห์', 'Analyzed by'], ['ผู้รับรองผล', 'Certified by'])
  ];
}

const tpl = (id: string, th: string, cat: TplCatId, kw: string, size: { w: number; h: number }, bg: string, els: TplEl[]): BuiltinTemplate =>
  ({ id, th, cat, kw, ...size, bg, els });

export const BUILTIN_TEMPLATES: BuiltinTemplate[] = [
  /* ---------- ร้านอาหาร · กาแฟ · เมนู ---------- */
  tpl('cafe-latte', 'ลาเต้แก้วโปรด', 'food', 'กาแฟ ลาเต้ คาเฟ่', TALL, COFFEE.bg, photoTop(COFFEE, 'ลาเต้หอมกรุ่น', 'คั่วสดทุกเช้า เมล็ดจากดอยไทย', 'เปิด 7:00 – 18:00', 'แก้วกาแฟลาเต้ลายใบไม้ บนโต๊ะไม้')),
  tpl('cafe-new', 'เมนูใหม่ ร้านกาแฟ', 'food', 'กาแฟ เมนูใหม่ ราคา', POST, COFFEE.bg, splitRound(COFFEE, 'เมนูใหม่', 'คาราเมล\nมัคคิอาโต้', '฿65', 'แก้วกาแฟเย็นคาราเมล')),
  tpl('cafe-menu-a4', 'เมนูร้านกาแฟ A4', 'food', 'เมนู กาแฟ ราคา พิมพ์ A4', A4, COFFEE.bg, menuList(COFFEE, 'ร้านกาแฟของเรา',
    [['เอสเปรสโซ่', '45'], ['อเมริกาโน่', '50'], ['ลาเต้', '60'], ['คาปูชิโน่', '60'], ['มอคค่า', '65'], ['ชาไทย', '50'], ['ชาเขียวมัทฉะ', '65'], ['โกโก้', '55'], ['ครัวซองต์', '55'], ['เค้กช็อกโกแลต', '75']],
    'ราคาเป็นบาท · ทุกแก้วเพิ่มช็อตได้ +15', 'เมล็ดกาแฟคั่ว', frameH(A4))),
  tpl('matcha', 'มัทฉะเย็น', 'food', 'ชาเขียว มัทฉะ เครื่องดื่ม', TALL, MATCHA.bg, photoTop(MATCHA, 'มัทฉะเย็นชื่นใจ', 'ชาเขียวแท้ ไม่ใส่สี หวานน้อยได้', 'แก้วละ 65 บาท', 'แก้วชาเขียวมัทฉะเย็น', 'Mitr')),
  tpl('food-menu-post', 'เมนูอาหารตามสั่ง', 'food', 'อาหารตามสั่ง เมนู ราคา ข้าว', TALL, TOMATO.bg, menuList(TOMATO, 'อาหารตามสั่ง',
    [['กะเพราหมูสับไข่ดาว', '60'], ['ข้าวผัดกุ้ง', '70'], ['ผัดซีอิ๊วหมู', '60'], ['ต้มยำกุ้ง', '120'], ['ไข่เจียวหมูสับ', '50'], ['ผัดผักรวม', '50']],
    'เปิดทุกวัน 10:00 – 20:00 · โทรสั่งได้', 'จานกะเพราไข่ดาว', frameH(TALL))),
  tpl('food-hot', 'เมนูขายดี', 'food', 'อาหาร ขายดี ราคา', POST, TOMATO.bg, splitRound(TOMATO, 'ขายดีอันดับ 1', 'กะเพรา\nหมูกรอบ', '฿69', 'จานข้าวกะเพราหมูกรอบ ไข่ดาว')),
  tpl('bakery-story', 'เบเกอรี่ สตอรี่', 'food', 'เบเกอรี่ ขนม เค้ก สตอรี่', STORY, BERRY.bg, storyBand(BERRY, 'เค้กสตรอว์เบอร์รี่', 'อบใหม่ทุกวัน · สั่งทำเค้กวันเกิดได้', 'สั่งเลย', 'เค้กสตรอว์เบอร์รี่ครีมสด')),
  tpl('bubble-tea', 'ชานมไข่มุก', 'food', 'ชานม ไข่มุก เครื่องดื่ม', POST, BERRY.bg, product(BERRY, 'ชานม ไข่มุกนุ่ม', ['หวานน้อยได้', 'ไข่มุกต้มใหม่ทุกชั่วโมง', 'ส่งไวในเมือง'], 'แก้วชานมไข่มุก')),
  tpl('cafe-open', 'ร้านเปิดแล้ว', 'food', 'เปิดร้าน ร้านใหม่ ประกาศ คาเฟ่', POST, NIGHT.bg, notice(NIGHT, 'เปิดแล้ว!', 'ร้านใหม่ของเรา', 'เครื่องดื่ม ขนม และที่นั่งสบาย ๆ\nแวะมาทักทายกันได้นะคะ', 'ทุกวัน 8:00 – 20:00')),
  tpl('food-story-promo', 'อาหารสตอรี่', 'food', 'อาหาร สตอรี่ ส่งฟรี', STORY, TOMATO.bg, storyBand(TOMATO, 'ส่งฟรี! ในระยะ 3 กม.', 'สั่งครบ 200 บาท เดลิเวอรี่ถึงบ้าน', 'ทักแชทสั่งเลย', 'อาหารไทยหลายจานบนโต๊ะ')),

  /* ---------- โปรโมชั่น · ลดราคา ---------- */
  tpl('sale-50', 'ลดสูงสุด 50%', 'promo', 'ลด 50 เซลล์ ส่วนลด', POST, SUN.bg, saleBurst(SUN, 'ลดกระหน่ำ', '50%', 'วันนี้ – สิ้นเดือนนี้เท่านั้น', 'สินค้าวางเรียงบนชั้น')),
  tpl('sale-night', 'เซลล์กลางคืน', 'promo', 'เซลล์ ลดราคา กลางคืน ไลฟ์', POST, NIGHT.bg, saleBurst(NIGHT, 'ไลฟ์สดคืนนี้', '-30%', '20:00 น. เป็นต้นไป · ของแถมทุกออเดอร์', 'กล่องพัสดุ ของขวัญ')),
  tpl('buy1get1', 'ซื้อ 1 แถม 1', 'promo', 'ซื้อ1แถม1 แถม ของแถม', POST, PURPLE.bg, splitRound(PURPLE, 'โปรพิเศษ', 'ซื้อ 1\nแถม 1', 'ฟรี!', 'สินค้าสองชิ้นคู่กัน')),
  tpl('coupon', 'คูปองส่วนลด', 'promo', 'คูปอง บัตรกำนัล ส่วนลด voucher', WIDE, SUN.bg, voucher(SUN, '฿100', 'ใช้เป็นส่วนลดเมื่อซื้อครบ 500 บาท\nหมดเขต 31 ธ.ค.', 'CODE: SAVE100', 'ถุงช้อปปิ้ง')),
  tpl('promo-tall', 'โปรประจำเดือน', 'promo', 'โปรโมชั่น ประจำเดือน', TALL, SKY.bg, photoTop(SKY, 'โปรประจำเดือน', 'ซื้อครบ 1,000 บาท รับส่วนลดทันที 10%', 'ถึงสิ้นเดือนนี้', 'สินค้าขายดีวางบนพื้นสีฟ้า')),
  tpl('flash-story', 'แฟลชเซลล์ สตอรี่', 'promo', 'แฟลชเซลล์ สตอรี่ ลดราคา', STORY, SUN.bg, storyBand(SUN, 'แฟลชเซลล์ 24 ชม.', 'ลดทุกชิ้น · จำนวนจำกัด', 'กดสั่งเลย', 'สินค้าลดราคาพร้อมป้าย')),
  tpl('free-ship', 'ส่งฟรีทั่วไทย', 'promo', 'ส่งฟรี จัดส่ง', POST, SKY.bg, notice(SKY, 'ข่าวดี', 'ส่งฟรีทั่วไทย', 'เมื่อสั่งครบ 300 บาท\nไม่ต้องใช้โค้ด', 'ตั้งแต่วันนี้ถึงสิ้นเดือน')),
  tpl('new-arrival', 'สินค้าใหม่', 'promo', 'สินค้าใหม่ มาใหม่', POST, PURPLE.bg, product(PURPLE, 'สินค้าใหม่ มาแล้ว', ['ของแท้ 100%', 'รับประกัน 1 ปี', 'ผ่อน 0% ได้'], 'สินค้าใหม่บนแท่นโชว์')),
  tpl('member-day', 'วันสมาชิก', 'promo', 'สมาชิก แต้ม ส่วนลด', POST, BERRY.bg, saleBurst(BERRY, 'วันสมาชิก', 'x2', 'แต้มสะสมคูณสองทุกวันพุธ', 'บัตรสมาชิกกับถุงช้อปปิ้ง')),
  tpl('clearance', 'ล้างสต็อก', 'promo', 'ล้างสต็อก เคลียร์ ลดราคา', TALL, TOMATO.bg, tips(TOMATO, 'ล้างสต็อก ลดหนักมาก', ['ลดสูงสุด 70% ทุกหมวด', 'ซื้อ 3 ชิ้น ลดเพิ่ม 10%', 'สินค้ามีจำนวนจำกัด', 'ไม่รับเปลี่ยนคืน'], 'เฉพาะหน้าร้าน ถึงสิ้นเดือนนี้')),

  /* ---------- เกษตร (โพสต์) ---------- */
  tpl('durian-sale', 'ทุเรียนพร้อมส่ง', 'agri', 'ทุเรียน หมอนทอง ผลไม้ พร้อมส่ง', POST, DURIAN.bg, splitRound(DURIAN, 'ตัดสดจากสวน', 'ทุเรียน\nหมอนทอง', '฿180', 'ทุเรียนหมอนทองผ่าเห็นเนื้อสีเหลือง')),
  tpl('durian-story', 'ทุเรียน สตอรี่', 'agri', 'ทุเรียน สตอรี่ สั่งจอง', STORY, DURIAN.bg, storyBand(DURIAN, 'เปิดจองทุเรียน', 'ส่งตรงจากสวน คัดลูกสวย เนื้อแห้ง', 'สั่งจองเลย', 'ทุเรียนบนต้นในสวน')),
  tpl('fert-product', 'ปุ๋ยสูตรใหม่', 'agri', 'ปุ๋ย ปุ๋ยน้ำ ธาตุอาหาร สินค้า', POST, FARM.bg, product(FARM, 'ปุ๋ยน้ำ สูตรเร่งโต', ['ดูดซึมไว ใบเขียว', 'ใช้ได้ทั้งพืชผักและไม้ผล', 'ผสมน้ำ 20 ซีซี / 20 ลิตร'], 'ขวดปุ๋ยน้ำวางบนดินกับต้นกล้า')),
  tpl('farm-tips', 'เคล็ดลับดูแลสวน', 'agri', 'ความรู้ เคล็ดลับ ดูแล สวน', TALL, FARM.bg, tips(FARM, '4 เคล็ดลับ ดูแลต้นช่วงหน้าฝน', ['ระบายน้ำ อย่าให้น้ำขังโคนต้น', 'ตัดแต่งกิ่ง ให้แสงส่องถึง', 'เฝ้าระวังโรครากเน่า', 'ให้ปุ๋ยน้อยแต่บ่อยครั้ง'], 'แชร์ให้เพื่อนชาวสวนได้เลย')),
  tpl('soil-tips', 'ดินดี ผลผลิตดี', 'agri', 'ดิน ปุ๋ยอินทรีย์ ความรู้', TALL, SOIL.bg, tips(SOIL, 'ดินดี เริ่มที่ 4 ข้อนี้', ['ตรวจค่า pH ดินปีละครั้ง', 'เติมอินทรียวัตถุสม่ำเสมอ', 'คลุมดินลดการสูญเสียน้ำ', 'หมุนเวียนพืชที่ปลูก'], 'ปรึกษาเรื่องดินได้ ทักแชท')),
  tpl('veg-fresh', 'ผักสดจากไร่', 'agri', 'ผัก ผักสด ออร์แกนิก ไร่', TALL, MATCHA.bg, photoTop(MATCHA, 'ผักสด จากไร่ถึงบ้าน', 'ปลอดสาร เก็บเช้า ส่งบ่าย', 'สั่งได้ทุกวัน', 'ตะกร้าผักสดหลากสี', 'Mitr')),
  tpl('fruit-box', 'ผลไม้กล่องของขวัญ', 'agri', 'ผลไม้ กล่อง ของขวัญ มังคุด', POST, BERRY.bg, splitRound(BERRY, 'ของฝากจากสวน', 'มังคุด\nคัดเกรด', '฿250', 'มังคุดในกล่องของขวัญ')),
  tpl('farm-open', 'เปิดสวนให้เที่ยว', 'agri', 'สวน เที่ยวสวน บุฟเฟ่ต์ผลไม้ ประกาศ', POST, FARM.bg, notice(FARM, 'เปิดสวนแล้ว', 'บุฟเฟ่ต์ผลไม้ในสวน', 'เด็ก 100 · ผู้ใหญ่ 250 บาท\nทุกเสาร์–อาทิตย์', 'จองล่วงหน้า ทักแชท')),
  tpl('agri-promo', 'ปุ๋ยลดราคา', 'agri', 'ปุ๋ย ลดราคา โปรโมชั่น เกษตร', POST, FARM.bg, saleBurst(FARM, 'ปุ๋ยลดราคา', '15%', 'ซื้อ 10 กระสอบขึ้นไป ส่งฟรีในจังหวัด', 'กระสอบปุ๋ยวางเรียงในโกดัง')),
  tpl('rice-story', 'ข้าวใหม่ สตอรี่', 'agri', 'ข้าว ข้าวหอมมะลิ ข้าวใหม่ สตอรี่', STORY, SOIL.bg, storyBand(SOIL, 'ข้าวหอมมะลิ ข้าวใหม่', 'สีใหม่ทุกสัปดาห์ หอม นุ่ม', 'สั่งเลย', 'ทุ่งนาข้าวสีทองกับรวงข้าว')),

  /* ---------- โซเชียล · เทศกาลไทย ---------- */
  tpl('songkran', 'สุขสันต์วันสงกรานต์', 'social', 'สงกรานต์ ปีใหม่ไทย น้ำ เมษายน', POST, WATER.bg, greeting(WATER, 'สุขสันต์\nวันสงกรานต์', 'ขอให้ทุกท่านมีความสุข สดชื่นตลอดปี', 'drop')),
  tpl('loykrathong', 'ลอยกระทง', 'social', 'ลอยกระทง พฤศจิกายน กระทง', POST, NIGHT.bg, greeting(NIGHT, 'สุขสันต์\nวันลอยกระทง', 'ขอให้สิ่งไม่ดีลอยไปกับสายน้ำ', 'star5')),
  tpl('newyear', 'สวัสดีปีใหม่', 'social', 'ปีใหม่ new year มกราคม', POST, PURPLE.bg, greeting(PURPLE, 'สวัสดีปีใหม่', 'ขอบคุณที่อยู่ด้วยกันตลอดปีที่ผ่านมา', 'sparkle4')),
  tpl('cny', 'ตรุษจีน', 'social', 'ตรุษจีน จีน อั่งเปา', POST, GOLD.bg, greeting(GOLD, 'ซินเจียยู่อี่\nซินนี้ฮวดไช้', 'ขอให้ร่ำรวย เฮง ๆ ตลอดปี', 'flower8-thin', 'Pridi')),
  tpl('mother', 'วันแม่', 'social', 'วันแม่ แม่ สิงหาคม ดอกมะลิ', POST, MOM.bg, greeting(MOM, 'รักแม่\nที่สุดในโลก', '12 สิงหาคม วันแม่แห่งชาติ', 'flower6')),
  tpl('father', 'วันพ่อ', 'social', 'วันพ่อ พ่อ ธันวาคม', POST, DAD.bg, greeting(DAD, 'รักพ่อ', '5 ธันวาคม วันพ่อแห่งชาติ', 'heart')),
  tpl('thanks', 'ขอบคุณลูกค้า', 'social', 'ขอบคุณ ลูกค้า รีวิว', POST, LOTUS.bg, greeting(LOTUS, 'ขอบคุณ\nที่อุดหนุน', 'ทุกออเดอร์คือกำลังใจของเรา', 'heart', 'Mali')),
  tpl('holiday', 'ประกาศวันหยุด', 'social', 'ประกาศ วันหยุด ปิดร้าน', POST, SKY.bg, notice(SKY, 'ประกาศ', 'ร้านหยุดยาว', 'ปิด 12 – 16 เม.ย.\nเปิดตามปกติ 17 เม.ย.', 'ขออภัยในความไม่สะดวก')),
  tpl('tips-social', 'โพสต์ความรู้ 4 ข้อ', 'social', 'ความรู้ เคล็ดลับ โพสต์', TALL, PURPLE.bg, tips(PURPLE, 'หัวข้อความรู้ของคุณ', ['ข้อแรก เขียนสั้น ๆ ให้จำง่าย', 'ข้อสอง ยกตัวอย่างให้เห็นภาพ', 'ข้อสาม บอกสิ่งที่ควรเลี่ยง', 'ข้อสี่ ชวนให้ลงมือทำ'], 'กดแชร์เก็บไว้อ่านได้เลย')),
  tpl('story-social', 'สตอรี่ทั่วไป', 'social', 'สตอรี่ ไลฟ์ ติดตาม', STORY, PURPLE.bg, storyBand(PURPLE, 'ไลฟ์สดคืนนี้ 2 ทุ่ม', 'มาคุยกัน ถาม–ตอบ แจกของรางวัล', 'กดติดตาม', 'คนยิ้มถือโทรศัพท์ไลฟ์สด')),

  /* ---------- เอกสาร (A4) ---------- */
  tpl('doc-coa', 'ใบรับรองผลวิเคราะห์ (COA)', 'doc', 'COA ใบรับรอง ผลวิเคราะห์ certificate analysis lot ปุ๋ย', A4, '#ffffff', docCoa()),
  tpl('doc-spec', 'สเปคสินค้า', 'doc', 'สเปค ข้อมูลจำเพาะ specification สินค้า ธาตุอาหาร', A4, '#ffffff', docSpec()),
  tpl('doc-quote', 'ใบเสนอราคา', 'doc', 'ใบเสนอราคา quotation ราคา ขาย', A4, '#ffffff', docQuote()),
  tpl('doc-delivery', 'ใบส่งสินค้า', 'doc', 'ใบส่งสินค้า ใบส่งของ ใบกำกับ delivery note', A4, '#ffffff', docDelivery()),
  tpl('doc-soil', 'รายงานผลวิเคราะห์ดิน/ปุ๋ย', 'doc', 'ผลวิเคราะห์ ดิน ปุ๋ย น้ำ report soil lab ห้องแล็บ', A4, '#ffffff', docSoil())
];

/** a template by id (the panel, the tests) */
export const builtinById = (id: unknown) => BUILTIN_TEMPLATES.find(t => t.id === id) ?? null;

/** a template's own words: its name, its words, its texts and picture hints (search: these rank first) */
export function tplOwnWords(t: BuiltinTemplate): string {
  const texts = t.els.map(e => e.k === 'text' ? e.text : e.k === 'photo' ? e.hint : '').join(' ');
  return `${t.th} ${t.kw} ${texts}`.toLowerCase();
}
/** every word a template is found by: its own words and its category's */
export function tplWords(t: BuiltinTemplate): string {
  const c = TPL_CATS.find(x => x.id === t.cat)!;
  return `${tplOwnWords(t)} ${c.th} ${c.kw}`.toLowerCase();
}

/**
 * a template's ช่องใส่รูป in order (index 0 = the first `photo` in els): the size on its page in px, its hint and shape.
 * รูป AI (0022) counts slots this way on both sides — the Worker reads a slot's size and hint from here, not from the app.
 * A document's โลโก้ and ลายเซ็น slots (`noAi`) keep their index but are left out: only the member's own picture goes there.
 */
export function tplPhotoSlots(t: BuiltinTemplate): Array<{ index: number; w: number; h: number; hint: string; shape: 'rect' | 'round' | 'circle' }> {
  const k = t.w / 1080;
  return t.els.flatMap(e => e.k === 'photo' ? [e] : []).map((e, index) => ({ index, e }))
    .filter(({ e }) => !e.noAi)
    .map(({ e, index }) => ({ index, w: e.w * k, h: e.h * k, hint: e.hint, shape: e.shape ?? 'rect' }));
}

/** the fonts a template needs (loaded before it is built; a table's text is Sarabun, regular and bold) */
export const tplFonts = (t: BuiltinTemplate) =>
  [...new Set(t.els.flatMap(e => e.k === 'text' ? [`${e.weight}|${e.font}`] : e.k === 'table' ? ['400|Sarabun', '700|Sarabun'] : []))]
    .map(k => ({ family: k.split('|')[1], weight: Number(k.split('|')[0]) }));

/** a short fingerprint of a template's content (its preview picture is redone when it changes: tests/templates.test.ts) */
export function tplHash(t: BuiltinTemplate): string {
  const s = JSON.stringify(t);
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return (h >>> 0).toString(36);
}
